import { ReactNode, useMemo, useState } from 'react';

import { Sport } from '__generated__/globalTypes';
import { SEARCH_PARAMS } from 'components/search/InstantSearch/types';
import { useConfigContext } from 'contexts/config';
import { useSportContext } from 'contexts/sport';
import useDeck from 'hooks/decks/useDeck';
import useFeatureFlags from 'hooks/useFeatureFlags';
import useQueryString from 'hooks/useQueryString';
import { clearAll } from 'hooks/useVirtualToggle';
import { joinFiltersWithAnd, joinFiltersWithOr } from 'lib/algolia';
import { nonPlayableBlockchainRarities } from 'lib/cards';
import { FiltersGroup } from 'lib/filters';

import SearchCardsContextProvider from '.';
import useCompetitionEligibilityFilterValue from './useCompetitionEligibilityFilterValue';
import { useEarlyAccessFilterValues } from './useEarlyAccessFilterValues';
import useFavoriteFilterValue from './useFavoriteFilterValue';
import useGameweekFilterValue from './useGameweekFilterValue';
import { useNotInLineupFilterValue } from './useNotInLineupFilterValue';
import { useProbablePitchersFilterValue } from './useProbablePitchersFilterValue';

interface Props {
  children: ReactNode;
  includeCommonCards?: boolean;
  defaultFilters?: string;
  cardFilters: FiltersGroup[];
  advancedCardFilters?: FiltersGroup[];
  editableLists?: boolean;
  galleryOwnerSlug?: string;
}

const noObjectIds: string[] = [];

const SearchCardsProvider = ({
  children,
  includeCommonCards,
  defaultFilters,
  cardFilters,
  advancedCardFilters,
  editableLists,
  galleryOwnerSlug,
}: Props) => {
  const {
    flags: { useLegendMarketplaceFilter = false },
  } = useFeatureFlags();
  const { sport } = useSportContext();
  const [competitionEligibilityFilter, setCompetitionEligibilityFilter] =
    useState<string | false>(
      useQueryString(SEARCH_PARAMS.COMPETITION_ELIGIBILITY) ?? false
    );
  const [gameweekFilter, setGameweekFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.GAMEWEEK_FILTER) ?? false
  );
  const [probablePitchersFilter, setProbablePitchersFilter] = useState<
    string | false
  >(useQueryString(SEARCH_PARAMS.PROBABLE_PITCHERS_FILTERS) ?? false);
  const [customDecksFilter, setCustomDecksFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.DECK) ?? false
  );

  const [contenderFilter, setContenderFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.CONTENDER_LAUNCH) ?? false
  );

  const [challengerFilter, setChallengerFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.CHALLENGER_LAUNCH) ?? false
  );

  const [championFilter, setChampionFilter] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.CHAMPION_LAUNCH) ?? false
  );

  const [firstEditionWeekFilter, setFirstEditionWeekFilter] = useState<
    string | false
  >(useQueryString(SEARCH_PARAMS.FIRST_EDITION_WEEK) ?? false);

  const { deck } = useDeck({
    sport: sport || Sport.FOOTBALL,
    name: customDecksFilter || undefined,
    skip: !editableLists,
  });
  const deckTokens = deck?.cards.nodes;

  const [favoriteFilter, setFavoriteFilter] = useState<boolean>(
    Boolean(useQueryString(SEARCH_PARAMS.FAVORITE_FILTER))
  );
  const [notInLineUpFilter, setNotInLineUpFilter] = useState<boolean>(
    Boolean(useQueryString(SEARCH_PARAMS.NOT_IN_LINEUP))
  );
  const [nonPlayableCards, setNonPlayableCards] = useState<boolean>(
    Boolean(useQueryString(SEARCH_PARAMS.NON_PLAYABLE_CARDS))
  );
  const [legend, setLegend] = useState<boolean>(
    Boolean(useQueryString(SEARCH_PARAMS.LEGEND))
  );

  const { marketplacePromotionalEvents } = useConfigContext();
  const [promotion, setPromotion] = useState<string | false>(
    useQueryString(SEARCH_PARAMS.PROMOTION) ?? false
  );
  const competitionEligibilityFilterValue =
    useCompetitionEligibilityFilterValue(
      competitionEligibilityFilter || undefined
    );
  const gameweekFilterValue = useGameweekFilterValue(
    gameweekFilter || undefined
  );

  const probablePitchersFilterValue = useProbablePitchersFilterValue({
    probablePitchersFilterValue: probablePitchersFilter || undefined,
    skip: sport !== Sport.BASEBALL,
  });

  const { championFilterValue, contenderFilterValue, challengerFilterValue } =
    useEarlyAccessFilterValues({
      contenderFilter,
      challengerFilter,
      championFilter,
    });

  const firstEditionWeekFilterValue = firstEditionWeekFilter
    ? `serial_number:1`
    : null;

  const favoriteFilterValue = useFavoriteFilterValue({ skip: !favoriteFilter });
  const notInLineupFilterValue = useNotInLineupFilterValue({
    skip: !notInLineUpFilter,
  });

  const filterWidgets = [
    ...(cardFilters.flatMap(gf => gf.filters) || []),
    ...(advancedCardFilters?.flatMap(gf => gf.filters) || []),
  ];
  const collectibleFilterWidget = filterWidgets.find(
    w => w.type === 'virtualToggle' && w.key === 'collectible'
  );

  const hasCollectibleFilter = !!collectibleFilterWidget;

  const promotedCards = useMemo(
    () =>
      marketplacePromotionalEvents
        .find(e => e.sport === sport)
        ?.events?.find(e => e.name === promotion)?.objectIds || noObjectIds,
    [marketplacePromotionalEvents, promotion, sport]
  );

  const nonPlayableCardsFilters =
    hasCollectibleFilter &&
    !nonPlayableCards &&
    joinFiltersWithAnd(
      nonPlayableBlockchainRarities.map(rarity => `NOT rarity:${rarity}`)
    );

  const legendFilters =
    useLegendMarketplaceFilter &&
    hasCollectibleFilter &&
    !legend &&
    'NOT card_edition.display_name:Legend';

  const filters = useMemo(
    () =>
      [
        defaultFilters,
        includeCommonCards === false && 'NOT rarity:common',
        nonPlayableCardsFilters,
        legendFilters,
        customDecksFilter &&
          joinFiltersWithOr([
            ...(deckTokens?.map(({ slug }) => `slug:"${slug}"`) || []),
            `custom_decks.name:"${customDecksFilter}"`,
          ]),
        competitionEligibilityFilter && competitionEligibilityFilterValue,
        gameweekFilter && gameweekFilterValue,
        favoriteFilter && favoriteFilterValue,
        probablePitchersFilter && probablePitchersFilterValue,
        notInLineUpFilter && notInLineupFilterValue,
        firstEditionWeekFilter && firstEditionWeekFilterValue,
        championFilter && championFilterValue,
        contenderFilter && contenderFilterValue,
        challengerFilter && challengerFilterValue,
        promotedCards.length > 0 &&
          joinFiltersWithOr(promotedCards.map(id => `objectID:"${id}"`)),
      ].filter(Boolean),
    [
      defaultFilters,
      includeCommonCards,
      nonPlayableCardsFilters,
      legendFilters,
      customDecksFilter,
      deckTokens,
      competitionEligibilityFilter,
      competitionEligibilityFilterValue,
      gameweekFilter,
      gameweekFilterValue,
      favoriteFilter,
      favoriteFilterValue,
      probablePitchersFilter,
      probablePitchersFilterValue,
      notInLineUpFilter,
      notInLineupFilterValue,
      firstEditionWeekFilter,
      firstEditionWeekFilterValue,
      championFilter,
      championFilterValue,
      contenderFilter,
      contenderFilterValue,
      challengerFilter,
      challengerFilterValue,
      promotedCards,
    ]
  );

  const clearFilters = () => {
    setCompetitionEligibilityFilter(false);
    setGameweekFilter(false);
    setCustomDecksFilter(false);
    setFavoriteFilter(false);
    setNotInLineUpFilter(false);
    setNonPlayableCards(false);
    setPromotion(false);
    setProbablePitchersFilter(false);
    clearAll();
  };

  const value = useMemo(
    () => ({
      filters,
      competitionEligibilityFilter,
      setCompetitionEligibilityFilter,
      gameweekFilter,
      setGameweekFilter,
      probablePitchersFilter,
      setProbablePitchersFilter,
      firstEditionWeekFilter,
      setFirstEditionWeekFilter,
      customDecksFilter,
      setCustomDecksFilter,
      contenderFilter,
      setContenderFilter,
      challengerFilter,
      setChallengerFilter,
      championFilter,
      setChampionFilter,
      favoriteFilter,
      setFavoriteFilter,
      notInLineUpFilter,
      setNotInLineUpFilter,
      hasCollectibleFilter,
      nonPlayableCards,
      setNonPlayableCards,
      legend,
      setLegend,
      promotion,
      setPromotion,
      clearFilters,
      includeCommonCards,
      editableLists,
      galleryOwnerSlug,
    }),
    [
      filters,
      competitionEligibilityFilter,
      gameweekFilter,
      probablePitchersFilter,
      firstEditionWeekFilter,
      customDecksFilter,
      contenderFilter,
      challengerFilter,
      championFilter,
      favoriteFilter,
      notInLineUpFilter,
      hasCollectibleFilter,
      nonPlayableCards,
      legend,
      promotion,
      includeCommonCards,
      editableLists,
      galleryOwnerSlug,
    ]
  );

  return (
    <SearchCardsContextProvider value={value}>
      {children}
    </SearchCardsContextProvider>
  );
};

export default SearchCardsProvider;
